import { type SxProps, type Theme } from '@mui/material/styles';
import {
  DataGridPremium,
  GridFeatureMode,
  GridRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';

import { useOptimalResultsHeight } from '@vestigas/utils';

import { slotProps, sx } from './style';
import { getRowClassName, paginationModel, slots } from './tableConfig';
import { TableProps } from './types';

export function DataTable<T extends GridRowModel>({
  height,
  minHeight = 240,
  slots: slotsToExtend,
  slotProps: slotPropsToExtend,
  sx: sxToExtend,
  ...props
}: TableProps<T>) {
  const { resultsHeight, resultsWrapperRef } =
    useOptimalResultsHeight(minHeight);
  const internalApiRef = useGridApiRef();

  const apiRef = props.apiRef ?? internalApiRef;
  const calculatedHeight = height ?? resultsHeight;

  const getFeatureMode = (callback: unknown): GridFeatureMode =>
    callback ? 'server' : 'client';

  const sortingMode = getFeatureMode(props.onSortModelChange);
  const paginationMode = getFeatureMode(props.onPaginationModelChange);
  const filterMode = getFeatureMode(props.onFilterModelChange);
  const disableRowSelectionOnClick =
    props.disableRowSelectionOnClick ?? props.checkboxSelection;
  const isClickable = !!props.onRowClick;
  const isGroupedColumn = !!props.groupingColDef;
  const tableSx = [sx, sxToExtend] as SxProps<Theme>;

  return (
    <div
      className="overflow-x-auto"
      style={{ height: calculatedHeight }}
      ref={resultsWrapperRef}
    >
      <DataGridPremium<T>
        apiRef={apiRef}
        columnHeaderHeight={56}
        disableColumnFilter
        disableColumnMenu
        disableColumnResize={false}
        disableColumnSelector
        disableDensitySelector
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        filterMode={filterMode}
        getRowClassName={(params) =>
          getRowClassName(params, isGroupedColumn, apiRef, isClickable)
        }
        pagination
        paginationMode={paginationMode}
        paginationModel={paginationModel}
        rowHeight={48}
        slotProps={{ ...slotProps, ...slotPropsToExtend }}
        slots={{ ...slots, ...slotsToExtend }}
        sortingMode={sortingMode}
        {...props}
        sx={tableSx}
      />
    </div>
  );
}
